import { Skeleton } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import WidgetsIcon from "@mui/icons-material/Widgets";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainHeading from "../../../components/main-heading.component";

import "../content-engine.css";
import { contentEngineService } from "../content-engine.service";
import { _get, _post } from "../../../services/api.service";
import "../../video-create/CreateVideo.css";
import "./GenerateScripts.css";

const quickExamples: string[] = [
  "AI Agents",
  "Passive Income",
  "iPhone Tips",
  "History Facts",
  "Startup Lessons",
];

interface ContinueTopicStages {
  topic: boolean;
  format: boolean;
  scripts: boolean;
  refinement: boolean;
}

interface ContinueTopic {
  id: number;
  title: string;
  status: string;
  last_activity: string | null;
  stages: ContinueTopicStages;
  latest_content_id: number | null;
  content_format: string | null;
}

const STAGE_LABELS: Record<keyof ContinueTopicStages, string> = {
  topic: "Topic",
  format: "Format",
  scripts: "Scripts",
  refinement: "Video",
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const GenerateScripts: React.FC = () => {
  const [topic, setTopic] = useState<string>("");
  const [generating, setGenerating] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [continueTopics, setContinueTopics] = useState<ContinueTopic[]>([]);
  const navigate = useNavigate();
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    (async () => {
      try {
        const response = await _get("/content-engine/dashboard/", {}, true);
        if (response) {
          setContinueTopics((response.continue_creating || []).slice(0, 3));
        }
      } catch {
        // silently fail — page works without dashboard data
      } finally {
        setDashboardLoading(false);
      }
    })();
  }, []);
  const steps = [
    { label: "Topic", description: "Enter your topic idea" },
    { label: "Format", description: "Choose your content format" },
    { label: "Scripts", description: "Pick from generated scripts" },
    { label: "Refinement", description: "Refine and finalize your script" },
  ];
  const currentStep = 0;
  const handleGenerate = async (): Promise<void> => {
    if (topic.trim().length < 2) return;
    setGenerating(true);
    try {
      const response = await _post("/content-engine/topics/", { title: topic }, {}, false, true);
      const topicId = response?.id;
      if (!topicId) {
        throw new Error("Topic ID not returned");
      }
      contentEngineService.setSelectedTopic(topicId, topic);
      navigate("/content-engine/select-format");
    } catch {
      // error already handled by api.service
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="container-fluid generate-scripts-page">
      <MainHeading
        title="Generate Scripts"
        subTitle="Create compelling scripts from any topic in just seconds"
      />
      <div className="cv-workspace">
        <aside className="cv-progress-panel" aria-label="Content engine progress">
          <div className="cv-progress-caption">CONTENT ENGINE</div>
          {steps.map((step, index) => {
            const complete = index < currentStep;
            const active = index === currentStep;
            return (
              <div
                key={step.label}
                className={`cv-progress-step${complete ? " complete" : ""}${active ? " active" : ""}`}
              >
                <div className="cv-progress-rail">
                  <span>{complete ? "✓" : index + 1}</span>
                </div>
                <div>
                  <div className="cv-progress-title">{step.label}</div>
                  <div className="cv-progress-description">{step.description}</div>
                </div>
              </div>
            );
          })}
        </aside>

        <a
          className="inspire-link ce-mobile-nav-link"
          href="/files/theunplan-user-documentation.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 15, display: "flex" }} />
          <span className="guide-text">Content Engine Guide</span>
        </a>

        <section className="cv-creator-card">
          <div className="cv-card-inner">
            {generating ? (
              <div className="skeleton-wrap">
                <Skeleton variant="rounded" height={24} width="60%" sx={{ mb: 2 }} />
                <Skeleton variant="rounded" height={100} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="40%" sx={{ mb: 2 }} />
                <Skeleton variant="rounded" height={44} width={200} sx={{ mb: 3 }} />
                <Skeleton variant="text" width="25%" sx={{ mb: 1 }} />
                <div style={{ display: "flex", gap: 10 }}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} variant="rounded" height={32} width={120} />
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="subtitle-row">
                  <p className="subtitle">
                    What topic should we write scripts about?
                  </p>
                  <a
                    className="inspire-link ce-desktop-nav-link"
                    href="/files/theunplan-user-documentation.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <DescriptionOutlinedIcon sx={{ fontSize: 15, display: "flex" }} />
                    <span className="guide-text">Content Engine Guide</span>
                  </a>
                </div>
                <div className="input-wrap">
                  <textarea
                    maxLength={100}
                    placeholder="e.g. Burn rate issues, AI agents, travel hacks..."
                    value={topic}
                    className="text-area"
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setTopic(e.target.value)
                    }
                  />
                  <div className="char-counter">{topic.length} / 100</div>
                </div>
                <div className="tip-text">
                  <LightbulbOutlinedIcon sx={{ fontSize: 15, verticalAlign: "middle" }} />{" "}
                  Short, specific topics usually produce better scripts.
                </div>

                <div className="cv-actions ce-generate-actions">
                  <button
                    className="cv-btn cv-btn-primary"
                    disabled={topic.trim().length < 2 || generating}
                    onClick={handleGenerate}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "text-top" }} />
                    Generate 4 Script Ideas
                  </button>
                </div>

                <div className="divider">Explore popular topics</div>

                <div className="chips">
                  <div
                    className="chip"
                    onClick={() => navigate("/content-engine/trending-ideas")}
                  >
                    <WhatshotIcon sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }} /> Trending Ideas
                  </div>
                  <div
                    className="chip"
                    onClick={() => navigate("/content-engine/browse-by-niche")}
                  >
                    <WidgetsIcon sx={{ fontSize: 16, display: "flex" }} /> Browse categories
                  </div>
                </div>

                <div className="quick">
                  <div className="quick-head">
                    <div className="quick-title">Quick start examples</div>
                  </div>

                  <div className="quick-list">
                    {quickExamples.map((item: string) => (
                      <div
                        key={item}
                        className="quick-item"
                        onClick={() => setTopic(item)}
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
            {!generating && !dashboardLoading && continueTopics.length > 0 && (
              <div className="home-section">
                <div className="home-section-header">
                  <div className="home-section-title">Continue Working</div>
                </div>
                <div className="home-continue-list">
                  {continueTopics.map((t) => (
                    <div
                      key={t.id}
                      className="home-continue-item"
                      onClick={async () => {
                        contentEngineService.setSelectedTopic(t.id, t.title);
                        if (!t.stages.format) {
                          navigate("/content-engine/select-format");
                          return;
                        }
                        try {
                          const response = await _get(`/content-engine/topics/${t.id}/contents/`, {}, true);
                          const contents = response?.results || [];
                          if (contents.length > 0) {
                            contentEngineService.setGeneratedContents(t.id, contents);
                            if (t.stages.scripts && t.latest_content_id) {
                              const selected = contents.find((c: any) => c.id === t.latest_content_id);
                              if (selected) contentEngineService.setSelectedScript(selected);
                              navigate("/content-engine/script-detail");
                              return;
                            }
                          }
                        } catch {}
                        navigate("/content-engine/choose-script");
                      }}
                    >
                      <div className="home-continue-body">
                        <div className="home-continue-title-row">
                          <div className="home-continue-dot" data-status={t.status} />
                          <div className="home-continue-title">{capitalize(t.title)}</div>
                          {t.content_format && (
                            <span className="home-script-format">{t.content_format}</span>
                          )}
                        </div>
                        <div className="home-continue-body-content">
                          <div className="home-progress-bar">
                            <div className="home-progress-track">
                              {(Object.keys(STAGE_LABELS) as (keyof ContinueTopicStages)[]).map((key) => (
                                <div
                                  key={key}
                                  className={`home-progress-step${t.stages[key] ? " done" : ""}`}
                                  title={STAGE_LABELS[key]}
                                />
                              ))}
                            </div>
                            <span className="home-progress-label">
                              {Object.values(t.stages).filter(Boolean).length}/4
                            </span>
                          </div>
                          <div className="home-continue-meta">
                            {t.last_activity || ""} ·{" "}
                            {t.status === "refinement"
                              ? "Modified"
                              : t.status === "scripts_selected"
                                ? "Script selected"
                                : "Not used yet"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default GenerateScripts;
